import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

type SelectionOptionProps = {
  /** The option data to display */
  option: MultiSelectOptionT;
  /** Whether this option is currently selected */
  isSelected: boolean;
  /** Whether this option should be disabled */
  isDisabled: boolean;
  /** Callback when this option is selected */
  onSelect: (value: string) => void;
};

/**
 * Individual selectable option in the dropdown list.
 *
 * Plain button (no cmdk) with a checkbox indicator and label, styled like our
 * other dropdown lists.
 */
export function SelectionOption({
  option,
  isSelected,
  isDisabled,
  onSelect,
}: SelectionOptionProps) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      disabled={isDisabled}
      onClick={() => onSelect(option.value)}
      className={cn(
        "flex w-full items-center gap-2 rounded-none border-t border-t-[var(--c-border-soft-2)] px-3 py-2.5 text-left text-[var(--c-text)] first:border-t-0 hover:bg-[var(--c-surface)]",
        isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {/* Checkbox indicator */}
        <div
          className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border",
            isSelected
              ? "border-[var(--c-accent)] bg-[var(--c-accent)]"
              : "border-[var(--c-border-strong)]"
          )}
        >
          {isSelected && <Check className="h-3 w-3 text-white" />}
        </div>

        {/* Option label */}
        <div className="flex min-w-0 flex-col">
          <span
            className={cn(
              "truncate text-sm",
              !option.meta && option.label.includes("@")
                ? "lowercase"
                : "capitalize"
            )}
          >
            {option.label}
          </span>
          {option.meta && (
            <span
              className={cn(
                "text-[12px] text-[var(--c-muted)]",
                option.metaUppercase ? "uppercase" : "lowercase"
              )}
            >
              {option.meta}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
